import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { fetchReferenceData } from '../redux/slices/referencesSlice';
import { setFormDescription } from '../redux/slices/formSlice';
import {
  HOW_IT_WORKS,
  IMG,
  FEATURES_LIST,
  KEY_FEATURES,
  bookingLinkSrc,
  SUPPORT,
  LANDING_SECTION_IDS,
  SUPPORT_LIST_FIELDS,
} from '../const';
import { maxLength } from '../const/formConst';
import { routes } from '../const/routes';
import '../assets/styles/app.scss';
import Accordion from '../components/accordion';
import Banner from '../components/banner';
import CircleVideo from '../components/video';
import Expert from '../components/expert';
import Media from '../components/Media';
import SchemaBlock from '../components/schema-block';
import HiddenCardsBlock from '../components/hidden-cards';
import ModalWindow from '../components/modal-window';
import Counter from '../components/counter';

const ShortDescription = () => (
  <ul>
    <li>💡 Just describe your idea</li>
    <li>⏳ Takes less than 15 min</li>
    <li>
      🔥 <span className="no-text-transform">AI</span> trained on real projects
    </li>
  </ul>
);

const Landing = ({ setSelectedSection }) => {
  const dispatch = useDispatch();
  const { register, handleSubmit } = useForm();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const navigate = useNavigate();
  const { types, domains, loading, success, failed } = useSelector(
    (s) => s.references,
  );
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const cleanId = hash.replace('#', '');
      setSelectedSection(cleanId);
      setTimeout(() => scrollTo(cleanId), 0);
    }
  }, [hash]);

  useEffect(() => {
    if (
      !loading &&
      !success &&
      !failed &&
      (types.length < 1 || domains.length < 1)
    ) {
      dispatch(fetchReferenceData());
    }
  }, [types, domains, loading, success, failed]);

  useEffect(() => {
    if (isOpenModal) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [isOpenModal]);

  // Change state of anchor links in header
  useEffect(() => {
    const observers = [];

    const navButtons = [
      ...LANDING_SECTION_IDS,
      { id: 'hero' },
      { id: 'estimation' },
    ];

    const options = { rootMargin: '-30% 0px -60% 0px', threshold: 0 };

    navButtons.forEach((button) => {
      const element = document.getElementById(button.id);
      if (!element) return;

      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) setSelectedSection(button.id);
      }, options);

      observer.observe(element);
      observers.push({ observer, element });
    });

    return () =>
      observers.forEach(({ observer, element }) => observer.unobserve(element));
  }, []);

  const scrollDown = () => {
    scrollTo(`page-down`);
  };

  const scrollTo = (selection) => {
    document
      .querySelector(`#${selection}`)
      .scrollIntoView({ behavior: 'smooth' });
  };

  const handleCloseModal = () => {
    setModalType('');
    setIsOpenModal(false);
  };

  const handleOpenVideo = () => {
    setModalType('video');
    setIsOpenModal(true);
  };

  const handleOpenContactForm = () => {
    setModalType('contactForm');
    setIsOpenModal(true);
  };

  const handleOpenReportExample = () => {
    setModalType('report');
    setIsOpenModal(true);
  };

  const onSubmit = (data) => {
    if (data.description) {
      dispatch(setFormDescription(data));
    }

    navigate(routes.formPage);
  };

  return (
    <>
      <main>
        <div className="section page-heading" id="hero">
          <div className="page-heading__flex">
            <div>
              <h1>
                <strong>
                  Cost Calculator <span>for</span>
                </strong>{' '}
                Software Projects
              </h1>
              <ShortDescription />
            </div>
            <CircleVideo />
          </div>

          <Media query="(max-width: 900px)">
            {(match) =>
              match ? (
                <div className="btn-open-report__mobile-wrapper">
                  <p>
                    <strong>Want to see the report?</strong>
                  </p>
                  <button
                    type="button"
                    className="btn-open-report"
                    onClick={handleOpenReportExample}
                  >
                    Review an example
                  </button>
                </div>
              ) : null
            }
          </Media>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="page-heading__input-block"
          >
            <input
              type="text"
              {...register('description')}
              placeholder="I want to build..."
              maxLength={maxLength}
            />
            <Media query="(max-width: 900px)">
              {(match) => (match ? <Counter /> : null)}
            </Media>

            <button type="submit" className="primaryFatLink ic-arrow-up">
              Calculate
            </button>
          </form>
          <div className="page-heading__bottom">
            <Media query="(min-width: 901px)">
              {(match) =>
                match ? (
                  <>
                    <Counter />
                    <HiddenCardsBlock
                      setSelectedSection={scrollTo}
                      openReportExample={handleOpenReportExample}
                    />
                    <button
                      type="button"
                      className="page-heading__scroll-down"
                      title="Scroll"
                      onClick={scrollDown}
                    />
                  </>
                ) : null
              }
            </Media>
          </div>
        </div>

        <section className="section" id="page-down">
          <p className="dash-before-text">
            With 10+ years of experience and an AI-powered tool backed by 500+
            projects' historical data, we deliver accurate ballpark project
            estimates within minutes.
          </p>
        </section>

        <section className="section pt" id={LANDING_SECTION_IDS[0].id}>
          <div className="section-heading">
            <h2>Key features</h2>
          </div>
          <ul className="key-features-list">
            {KEY_FEATURES.map((item) => (
              <li key={item.id}>
                <div>
                  <img
                    src={`${IMG.QUICK_QUOTE}/${item.image}.svg`}
                    width="288"
                    height="194"
                    loading="lazy"
                  />
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className="wide-banner__wrapper">
          <section className="section">
            <div className="wide-banner__video-wrapper">
              <div className="wide-banner__video-wrapper__bg">
                <picture>
                  <source
                    media="(max-width: 480px)"
                    srcSet={`${IMG.QUICK_QUOTE}/banner-video-poster-mob.avif`}
                    type="image/avif"
                  />
                  <source
                    media="(max-width: 480px)"
                    srcSet={`${IMG.QUICK_QUOTE}/banner-video-poster-mob.webp`}
                    type="image/webp"
                  />
                  <source
                    media="(max-width: 480px)"
                    srcSet={`${IMG.QUICK_QUOTE}/banner-video-poster-mob.png`}
                    type="image/png"
                  />

                  <source
                    media="(min-width: 481px)"
                    srcSet={`${IMG.QUICK_QUOTE}/banner-video-poster.avif`}
                    type="image/avif"
                  />
                  <source
                    media="(min-width: 481px)"
                    srcSet={`${IMG.QUICK_QUOTE}/banner-video-poster.webp`}
                    type="image/webp"
                  />
                  <source
                    media="(min-width: 481px)"
                    srcSet={`${IMG.QUICK_QUOTE}/banner-video-poster.png`}
                    type="image/png"
                  />
                  <img
                    src={`${IMG.QUICK_QUOTE}/banner-video-poster.png`}
                    alt="Video poster"
                    width="457"
                    height="457"
                    loading="lazy"
                  />
                </picture>
                <button type="button" onClick={handleOpenVideo} title="Play" />
              </div>
            </div>
            <div className="wide-banner__text">
              <h2>Experience Lasoft AI's&nbsp;speed and accuracy firsthand</h2>
              <p>
                We help estimate your budget and resources you need in minutes.
                Get&nbsp;instant estimates for your software project — no
                meetings, no waiting.
              </p>
              <div className="wide-banner__link">
                <Link to={routes.formPage}>Project Estimate</Link>
              </div>
            </div>
          </section>
        </div>

        <section className="section pt" id={LANDING_SECTION_IDS[1].id}>
          <div className="section-heading">
            <h2>How cost calculator works</h2>
          </div>
          <ul className="hiw-list">
            {HOW_IT_WORKS.map((item) => (
              <li key={item.id}>
                <p>{item.title}</p>
                <div>
                  <picture>
                    <source
                      srcSet={`${IMG.QUICK_QUOTE}/${item.image}.avif`}
                      type="image/avif"
                    />
                    <source
                      srcSet={`${IMG.QUICK_QUOTE}/${item.image}.webp`}
                      type="image/webp"
                    />
                    <source
                      srcSet={`${IMG.QUICK_QUOTE}/${item.image}.png`}
                      type="image/png"
                    />
                    <img
                      src={`${IMG.QUICK_QUOTE}/${item.image}.png`}
                      alt={item.title}
                      width="288"
                      height="194"
                      loading="lazy"
                    />
                  </picture>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <div className="overflow-hidden">
          <section className="section pt">
            <div className="section-heading with-badge">
              <h2>
                <strong>LaEstimator </strong> vs Traditional methods
              </h2>
              <p className="text-box">
                If you have rich historical data, it is possible to make a
                ballpark estimate within several hours or days.
              </p>
            </div>
            <div className="pricing-block-wrapper">
              <ul className="support">
                <li className="support-card">
                  <div className="support-card__heading">
                    <div className="support-card__heading-flex">
                      <div>
                        <h3>Plan Name</h3>
                        <p>
                          <strong>Price</strong>
                        </p>
                      </div>
                      <h4>Deliverables</h4>
                    </div>
                  </div>

                  <div>
                    <ul>
                      {SUPPORT_LIST_FIELDS.map((item) => (
                        <li key={item}>
                          <div>{item}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
                {SUPPORT.map((item) => (
                  <li key={item.id} className="support-card">
                    <div className="support-card__heading">
                      <div>
                        <h3 className="support-card__heading-title">
                          {item.name}
                        </h3>
                        <div className="support-card__heading-pricing">
                          <div>{item.pricing}</div>
                          <div>{item.time}</div>
                        </div>
                        <div className="support-card__heading-text">
                          <h4>Experts Support</h4>
                          <p>{item.experts}</p>
                        </div>
                        <div>
                          {item.is_contact_form ? (
                            <button
                              type="button"
                              className="secondaryFatLink ic-arrow-up"
                              onClick={handleOpenContactForm}
                            >
                              Get started
                            </button>
                          ) : (
                            <Link
                              to={routes.formPage}
                              className="primaryFatLink ic-arrow-up"
                            >
                              Get started
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <ul>
                        {Object.entries(item.options).map(([key, value]) => (
                          <li
                            key={key}
                            className={`${typeof value === 'string' ? '' : value ? 'checked' : 'unchecked'}`}
                          >
                            <div>{value}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="support-card__btn">
                      {item.is_contact_form ? (
                        <button
                          type="button"
                          className="secondaryFatLink ic-arrow-up"
                          onClick={handleOpenContactForm}
                        >
                          Get started
                        </button>
                      ) : (
                        <Link
                          to={routes.formPage}
                          className="primaryFatLink ic-arrow-up"
                        >
                          Get started
                        </Link>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </div>

        <Media query="(min-width: 901px)">
          {(match) =>
            match ? (
              <section className="section ballpark">
                <div className="section-heading">
                  <h2>Ballpark estimation vs binding project quote</h2>
                </div>
                <div className="two-columns">
                  <p>
                    <strong>
                      If you have rich historical data, it is possible to make a
                      ballpark estimate within several hours or days. Our AI
                      tool makes the estimation within minutes. However, with
                      this short amount of time and project understanding, you
                      can achieve only 60-70% of the accuracy in the estimation.
                    </strong>
                  </p>
                  <p>
                    Ballparks do not require a lot of effort, which is why we
                    offer them for free. A ballpark estimate gives you a good
                    overview of the possible budget or timeline.
                    A&nbsp;professional software development team can reduce or
                    increase the budget by playing with the project scope.
                  </p>
                </div>
                <SchemaBlock />
                <p className="text-center">
                  <strong>
                    In contrast to Ballpark estimation, which does not put any
                    obligations on the software development team, a Binding
                    Project Quote requires at least 80-90% accuracy. It requires
                    a close to 60-80 hours of a deep project dive to achieve
                    this level of accuracy.
                  </strong>
                </p>
              </section>
            ) : null
          }
        </Media>

        <div className="bg-img">
          <section className="section expert">
            <div className="section-heading">
              <h2>
                Book a call with the <strong>expert</strong>
              </h2>
            </div>
            <Expert />
          </section>
        </div>

        <section className="section" id={LANDING_SECTION_IDS[2].id}>
          <div className="faq-block">
            <div className="faq-block__heading">
              <h2>
                Frequently <strong>asked questions</strong>
              </h2>
            </div>
            <div className="faq-block__accordion">
              <Accordion />
            </div>
          </div>
        </section>

        <section className="section banner">
          <Banner />
        </section>

        <div className="bg-img" id="estimation">
          <section className="section">
            <ul className="featuresGrid">
              <li>
                <h2>Ready for 100% accurate software estimation?</h2>
                <p>
                  We prepared a clear roadmap for your product development,
                  accompanied by a detailed report that outlines your idea. Our
                  AI-powered estimation and expert analysis will help you move
                  forward with the project.
                </p>
              </li>
              {FEATURES_LIST.map((item) => (
                <li key={item.id} className="linearGradientBg">
                  <h3 className={`${item.icon}`}>{item.title}</h3>
                  <p>{item.text}</p>
                </li>
              ))}
              <li>
                <a
                  className="bookingLink ic-left-arrow"
                  href={bookingLinkSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book a free estimation call
                </a>
              </li>
            </ul>
          </section>
        </div>
      </main>
      {createPortal(
        <ModalWindow
          isOpen={isOpenModal}
          onClose={handleCloseModal}
          modalType={modalType}
        />,
        document.getElementById('root'),
      )}
    </>
  );
};

export default Landing;
